/**
 * Path-mode hover tooltip — listens to MOUSE_MOVE while path-mode is active,
 * scene-picks the entity under the cursor, looks up its calculation values
 * across all *active* load cases, picks the worst case (max `util`), and
 * publishes the result to `hoverInfoStore` for the floating tooltip component
 * to render.
 *
 * Lifecycle is owned by `pathMode.ts`: activate on enter, deactivate on exit,
 * and pause/resume around sub-tools so they can install their own MOUSE_MOVE
 * handlers without interference.
 */

import { Cartesian2, ScreenSpaceEventType, type Entity } from 'cesium';
import { writable } from 'svelte/store';

import { useCesiumViewer } from '../cesium-viewer';
import {
	type ILoadCase,
	type ILoadCaseCalculations,
	type ILoadCaseCrossbeamCalc,
	type ILoadCaseLineCalc,
	type ILoadCasePoleCalc,
	type ILoadCaseSupportWireCalc
} from '../map-tools-store';
import { ENTITY_ID_PREFIX } from '$lib/utils/constants';
import { pathModeState } from './path-mode-state.svelte';

type HoverEntityType = 'pole' | 'crossbeam' | 'supportWire' | 'line';

export type HoverInfoRow = { label: string; value: string };

export type HoverInfo = {
    /** The display name of the type of entity/component */
    entityType: string;
	/** Cursor position in viewport coordinates (already includes canvas offset). */
	x: number;
	y: number;
	/** Entity-specific header — operationalMarking for poles/etc., conductorType for lines. */
	header: string;
	/** Name of the load case the displayed values came from (worst-case util). */
	loadCaseName: string;
	/** Headline utilisation percentage (server-formatted, e.g. 85 = 85%). */
	util: number;
	/** Status string for lines, undefined for other types. */
	status?: string;
	rows: HoverInfoRow[];
};

export const hoverInfoStore = writable<HoverInfo | null>(null);

let installed = false;
let throttleActive = false;
let lastPickedEntity: Entity | null = null;
let lastEndPosition: { x: number; y: number } | null = null;
let unsubStore: (() => void) | null = null;
let onCanvasLeave: (() => void) | null = null;

// ── Public API ──────────────────────────────────────────────────────────────

export function activateHoverInfo(): void {
	const viewer = useCesiumViewer();
	if (!viewer) return;

	// Always (re)arm the MOUSE_MOVE action:
	// `removeAllScreenSpaceInputActions` (run by every tool toggle) and the sub-tools' own
	// teardown remove the action without telling this module. Only the listeners/effect below are one-time.
	viewer.screenSpaceEventHandler.setInputAction(
		handleMouseMove as (event: unknown) => void,
		ScreenSpaceEventType.MOUSE_MOVE
	);

	if (installed) return;
	installed = true;

	// Clear when cursor leaves the canvas — MOUSE_MOVE stops firing there and
	// the tooltip would otherwise stick around stale.
	const handler = () => hoverInfoStore.set(null);
	viewer.canvas.addEventListener('mouseleave', handler);
	onCanvasLeave = () => viewer.canvas.removeEventListener('mouseleave', handler);

	// When activeLoadCases (or the sectionInfo identity) change, recompute the
	// currently-shown info — the worst-case load case might now be different.
	// `$effect.root` keeps the effect alive for the module-level subscription;
	// the cleanup function returned from it is invoked from `deactivateHoverInfo`
	// via the stashed `unsubStore` reference.
	let prevKey = '';
	const stopRoot = $effect.root(() => {
		$effect(() => {
			const active = pathModeState.activeLoadCases;
			const sectionInfo = pathModeState.sectionInfo;
			const key = `${pathModeState.worstCaseMode}|${active.join('|')}|${sectionInfo === null ? 'null' : 'set'}`;
			if (key === prevKey) return;
			prevKey = key;
			if (lastPickedEntity && lastEndPosition) {
				updateHover(lastPickedEntity, lastEndPosition.x, lastEndPosition.y);
			}
		});
	});
	unsubStore = stopRoot;
}

export function deactivateHoverInfo(): void {
	if (!installed) return;
	installed = false;

	const viewer = useCesiumViewer();
	if (viewer) {
		viewer.screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
	}

	onCanvasLeave?.();
	onCanvasLeave = null;
	unsubStore?.();
	unsubStore = null;

	lastPickedEntity = null;
	lastEndPosition = null;
	hoverInfoStore.set(null);
}

// ── Internal: MOUSE_MOVE pipeline ───────────────────────────────────────────

function handleMouseMove(event: { endPosition: { x: number; y: number } }): void {
	if (throttleActive) return;
	throttleActive = true;
	setTimeout(() => {
		throttleActive = false;
	}, 50);

	const viewer = useCesiumViewer();
	if (!viewer) return;

	const picked = viewer.scene.pick(new Cartesian2(event.endPosition.x, event.endPosition.y));
	const entity =
		picked && typeof picked === 'object' && 'id' in picked && picked.id && typeof picked.id === 'object'
			? (picked.id as Entity)
			: null;

	lastEndPosition = { x: event.endPosition.x, y: event.endPosition.y };
	lastPickedEntity = entity;

	if (!entity) {
		hoverInfoStore.set(null);
		return;
	}

	updateHover(entity, event.endPosition.x, event.endPosition.y);
}

function updateHover(entity: Entity, canvasX: number, canvasY: number): void {
	hoverInfoStore.set(computeHoverInfo(entity, canvasX, canvasY));
}

function computeHoverInfo(
	entity: Entity,
	canvasX: number,
	canvasY: number
): HoverInfo | null {
	const sectionInfo = pathModeState.sectionInfo;
	if (!sectionInfo?.loadCases?.length) return null;
	const worstCaseMode = pathModeState.worstCaseMode;
	const activeNames = pathModeState.activeLoadCases;
	if (!worstCaseMode && activeNames.length === 0) return null;

	const parsed = parseEntityId(entity.id);
	if (!parsed || parsed.type === 'mastpoint') return null;

	// Worst case: in worstCaseMode ALL stored load cases are considered, otherwise only the
	// active ones. Poles are picked by largest calculated JBD (utilization as tie-break); other
	// components by largest utilization.
	let worst: { calc: CalcUnion; lcName: string } | null = null;
	for (const lc of sectionInfo.loadCases) {
		if (!worstCaseMode && !activeNames.includes(lc.name)) continue;
		const calcs = pickCalcArray(lc.calculations, parsed.type);
		const calc = calcs.find((c) => c.id === parsed.numericId);
		if (!calc) continue;
		let better: boolean;
		if (worst === null) {
			better = true;
		} else if (parsed.type === 'pole') {
			const jbd = (calc as { worstCaseDiameter: number }).worstCaseDiameter;
			const worstJbd = (worst.calc as { worstCaseDiameter: number }).worstCaseDiameter;
			better = jbd > worstJbd || (jbd === worstJbd && calc.util > worst.calc.util);
		} else {
			better = calc.util > worst.calc.util;
		}
		if (better) {
			worst = { calc, lcName: lc.name };
		}
	}
	if (!worst) return null;

	// Translate canvas-relative coords to viewport-relative so the tooltip can
	// be `position: fixed` against the body.
	const viewer = useCesiumViewer();
	if (!viewer) return null;
	const rect = viewer.canvas.getBoundingClientRect();

	const formatted = formatForType(parsed.type, worst.calc);
	return { 
        entityType: formatted.entityType,
		x: rect.left + canvasX,
		y: rect.top + canvasY,
		header: formatted.header,
		// The pole/crossbeam/guy-wire values in the stiff calculation are family aggregates (the
		// worst of direction 1/2) - the API labels them with the family name in usedCaseName, so
		// the hover does not claim a direction the data does not have.
		loadCaseName: (worst.calc as { usedCaseName?: string }).usedCaseName ?? worst.lcName,
		util: worst.calc.util,
		status: parsed.type === 'line' ? (worst.calc as ILoadCaseLineCalc).status : undefined,
		rows: formatted.rows
	};
}

// ── Internal: id parsing ────────────────────────────────────────────────────

function parseEntityId(
	id: string
): { type: HoverEntityType | 'mastpoint'; numericId: number } | null {
	// Loadcase entities are formatted as `<lcName> <prefix><id>` with exactly
	// one space immediately before the prefix (and the loadcase name itself
	// can contain spaces). lastIndexOf(' ') reliably extracts the suffix.
	const lastSpace = id.lastIndexOf(' ');
	const tail = lastSpace >= 0 ? id.substring(lastSpace + 1) : id;

	if (tail.startsWith(ENTITY_ID_PREFIX.LINE)) {
		return numericTail(tail, ENTITY_ID_PREFIX.LINE.length, 'line');
	}
	if (tail.startsWith(ENTITY_ID_PREFIX.POLE)) {
		return numericTail(tail, ENTITY_ID_PREFIX.POLE.length, 'pole');
	}
	if (tail.startsWith(ENTITY_ID_PREFIX.CROSSBEAM)) {
		return numericTail(tail, ENTITY_ID_PREFIX.CROSSBEAM.length, 'crossbeam');
	}
	if (tail.startsWith(ENTITY_ID_PREFIX.SUPPORT)) {
		return numericTail(tail, ENTITY_ID_PREFIX.SUPPORT.length, 'supportWire');
	}
    if (tail.startsWith(ENTITY_ID_PREFIX.VIRTUAL_SUPPORT)){
        return numericTail(tail, ENTITY_ID_PREFIX.VIRTUAL_SUPPORT.length, 'supportWire');
    }
	if (tail.startsWith(ENTITY_ID_PREFIX.MASTPOINT)) {
		return { type: 'mastpoint', numericId: 0 };
	}
	return null;
}

function numericTail(
	tail: string,
	prefixLen: number,
	type: HoverEntityType
): { type: HoverEntityType; numericId: number } | null {
	const num = parseInt(tail.substring(prefixLen), 10);
	return Number.isFinite(num) ? { type, numericId: num } : null;
}

// ── Internal: calc dispatch + formatting ────────────────────────────────────

type CalcUnion =
	| ILoadCasePoleCalc
	| ILoadCaseCrossbeamCalc
	| ILoadCaseSupportWireCalc
	| ILoadCaseLineCalc;

function pickCalcArray(
	calculations: ILoadCaseCalculations,
	type: HoverEntityType
): CalcUnion[] {
	switch (type) {
		case 'pole':
			return calculations.poles;
		case 'crossbeam':
			return calculations.crossbeams;
		case 'supportWire':
			return calculations.supportWires;
		case 'line':
			return calculations.lines;
	}
}

function formatForType(
	type: HoverEntityType,
	calc: CalcUnion
): { header: string; rows: HoverInfoRow[], entityType: string } {
	const f1 = (n: number) => n.toFixed(1);
	const f2 = (n: number) => n.toFixed(2);
	switch (type) {
		case 'pole': {
			const p = calc as ILoadCasePoleCalc;
			return {
                entityType: 'Stolpe',
				header: p.operationalMarking,
				rows: [
                    { label: 'Stolpehøyde', value: `${f2(p.visibleHeight)} m`},
                    { label: 'JBD / Kalkulert JBD', value: `${f1(p.diameter)} / ${f1(p.worstCaseDiameter)} mm`},
					{ label: 'Maks moment', value: `${f1(p.maxMoment)} kNm` },
					{ label: 'Defl. topp', value: `${f2(p.displacementTop)} mm` },
                    { label: 'Aksialkraft fot', value: `${f2(p.bottomAxialForce)} kN`},
                    { label: 'Materiale', value: `${p.material}`},
                    { label: 'Maks. bøyemoment', value: `${f1(p.maxMoment)} kNm`},
                    { label: 'Høyde maks. bøyemoment', value: `${f1(p.maxMomentHeight)} m`}
				]
			};
		}
		case 'crossbeam': {
			const c = calc as ILoadCaseCrossbeamCalc;
			return {
                entityType: 'Travers',
				header: c.name,
				rows: [
                    { label: 'Funksjon', value: `${c.function}`},
                    { label: 'Lengde', value: `${c.length} m`},
                    { label: 'Offset fra mastetopp', value: `${c.offsetFromMastTop} m`},
					{ label: 'Dim./maks. moment X', value: `${f1(c.dimMomentCapacityX)} / ${f1(c.maxMomentX)} kNm` },
					{ label: 'Dim./maks. moment Y', value: `${f1(c.dimMomentCapacityY)} / ${f1(c.maxMomentY)} kNm` },
					// Samme rekkefoelge som moment-radene: kapasitet / verdi.
					{ label: 'Dim./maks. torsjon', value: `${f1(c.dimTorsionCapacity)} / ${f1(c.torsion)} kNm` }
				]
			};
		}
		case 'supportWire': {
			const s = calc as ILoadCaseSupportWireCalc;
			return {
                entityType: 'Bardun',
				header: `Retning ${f1(s.bearingXY)}° fra stolpe`,
				rows: [
					{ label: 'Materiale', value: `${s.material}` },
					{ label: 'Lengde', value: `${f2(s.length)} m` },
                    { label: 'Stigningsvinkel', value: `${f1(s.bearingXZ)}°`},
                    { label: 'Stigning', value: `1:${f1(s.gradient)}`},
					{ label: 'Strekk', value: `${f1(s.tension)} kN` },
					{ label: 'Bruddlast', value: `${f1(s.ratedTensileStrength)} kN` }
				]
			};
		}
		case 'line': {
			const l = calc as ILoadCaseLineCalc;
			return {
                entityType: 'Ledning',
				header: l.conductorType,
				rows: [
					{ label: 'Strekk', value: `${f1(l.tension)} kN` },
					{ label: 'Pilhøyde', value: `${f2(l.sag)} m` },
					{ label: 'Min. bakke', value: `${f1(l.lowestDistanceLineTerrain)} m` },
                    { label: '% av UTS', value: `${f2(l.utsPercentage)} %`},
                    { label: 'Spenning', value: `${f2(l.voltage)} kV`}
				]
			};
		}
	}
}

// Type guard helpers re-exported for downstream consumers if needed later.
export type { ILoadCase };
